import React, { useState } from "react";
import { Box, Collapse, Link, Skeleton, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNewsByIsin } from "@rentablo/api/apiv1";
import { sanitizeHref } from "@helpers";

const MAX_EXPANDED_NEWS = 3;

export const NewsSection = ({ isin }: { isin?: string }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useNewsByIsin(
    { isin: isin! },
    { enabled: Boolean(isin) }
  );

  const feedItems = data?.feedItems ?? [];

  const firstItem = feedItems[0];
  const expandedItems = feedItems.slice(0, MAX_EXPANDED_NEWS);

  return (
    <Box
      mt={2}
      pt={3}
      pb={{ xs: 4, md: 0 }}
      sx={{
        borderTop: (theme) => `1px solid ${theme.palette.border.secondary}`,
      }}
    >
      <Typography variant="body4" color="textSecondary" mb={1} px={5}>
        {t("domains.investments.investmentTable.detailModal.latestNews")}
      </Typography>
      {isLoading ? (
        <Box display="flex" flexDirection="column" gap={1} px={5}>
          <Skeleton variant="text" width="80%" height={24} />
        </Box>
      ) : feedItems.length ? (
        <>
          {!expanded ? (
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              px={5}
            >
              <Link
                href={sanitizeHref(firstItem.link)}
                target="_blank"
                rel="noopener noreferrer"
                variant="body3"
                fontWeight={600}
                color="text.primary"
                noWrap
                sx={{
                  display: "block",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                &quot;{firstItem.title}&quot;
              </Link>
              <Link
                component="button"
                variant="body3"
                onClick={() => setExpanded(true)}
                sx={{ whiteSpace: "nowrap" }}
              >
                {t("domains.investments.investmentTable.detailModal.readMore")}
              </Link>
            </Box>
          ) : (
            <Collapse in={expanded}>
              <Box display="flex" flexDirection="column">
                {expandedItems.map((item, idx) => (
                  <Box
                    key={idx}
                    px={5}
                    py={2}
                    sx={{
                      borderBottom: (theme) =>
                        `1px solid ${theme.palette.border.secondary}`,
                    }}
                  >
                    <Link
                      href={sanitizeHref(item.link)}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body3"
                      fontWeight={600}
                      color="text.primary"
                    >
                      &quot;{item.title}&quot;
                    </Link>
                    {item.description && (
                      <Typography
                        variant="caption2"
                        color="textSecondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                ))}
                <Box px={5} py={2}>
                  <Link
                    component="button"
                    variant="body3"
                    onClick={() => setExpanded(false)}
                  >
                    {t(
                      "domains.investments.investmentTable.detailModal.readLess"
                    )}
                  </Link>
                </Box>
              </Box>
            </Collapse>
          )}
        </>
      ) : (
        <Box px={5}>
          <Typography variant="body3" color="textSecondary">
            {t("domains.investments.investmentTable.detailModal.noNews")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
